import { TableColumnsProp } from 'src/app/shared/models/tables.model';
import { DataTableConfig } from 'src/app/shared/components/data-table';
import {
  BulkDeleteItemNames,
  GridColumnsStorageNames,
  gridMessages,
  MessageTemplates,
} from 'src/app/shared/utility/constants';
import { AgentFilter, AgentRow } from './agent.model';
import { consigneeConfig } from '../../../shared/components/address/address.config';
import { FormConfig } from '../../../shared/models/dynamic.model';
import { ValidatorPattern } from '../../../shared/utility/validators.pattern';

export const agentsSummaryGridColumns: TableColumnsProp[] = [
  {
    key: 'code',
    label: 'Code',
    isSelected: true,
    defaultView: true,
  },
  {
    key: 'name',
    label: 'Name',
    isSelected: true,
    defaultView: true,
  },
  {
    key: 'types',
    label: 'Type',
    isSelected: true,
    defaultView: true,
  },
  {
    key: 'status',
    label: 'Status',
    isSelected: true,
    defaultView: true,
  },
  {
    key: 'serviceTypes',
    label: 'Service Types',
    isSelected: false,
    defaultView: false,
  },
  {
    key: 'fuelSurcharge',
    label: 'Fuel Surcharge',
    isSelected: false,
    defaultView: false,
  },
];

export interface AgentSummarySearchCriteria {
  searchText: string;
}

export const agentDataTableConfig: DataTableConfig<AgentRow> = {
  rowIdField: 'id',
  entityName: BulkDeleteItemNames.Agents,
  storageKey: GridColumnsStorageNames.agent,
  columns: [
    {
      key: 'code',
      label: 'Code',
      isSelected: true,
      defaultView: true,
      sortable: true,
      isHyperlink: true,
      defaultPrintWidth: 140,
      showTooltip: true,
    },
    {
      key: 'name',
      label: 'Name',
      isSelected: true,
      defaultView: true,
      sortable: true,
      defaultPrintWidth: 180,
      showTooltip: true,
    },
    {
      key: 'types',
      label: 'Type',
      isSelected: true,
      defaultView: true,
      sortable: true,
      defaultPrintWidth: 140,
    },
    {
      key: 'status',
      label: 'Status',
      isSelected: true,
      defaultView: true,
      sortable: true,
      defaultPrintWidth: 120,
    },
    {
      key: 'serviceTypes',
      label: 'Service Types',
      isSelected: false,
      defaultView: false,
      sortable: true,
      defaultPrintWidth: 160,
      showTooltip: true,
    },
    {
      key: 'fuelSurcharge',
      label: 'Fuel Surcharge',
      isSelected: false,
      defaultView: false,
      sortable: true,
      defaultPrintWidth: 140,
    },
  ],
  features: {
    selection: true,
    pagination: true,
    sorting: true,
    columnReorder: true,
    selectAllAcrossPages: true,
  },
  messages: {
    noSearch: `${gridMessages.noSearch} ${BulkDeleteItemNames.Agents}`,
    noMatch: gridMessages.noRecordsMatch(BulkDeleteItemNames.Agents),
    allPageSelected: MessageTemplates.AllItemsSelected,
    selectAll: MessageTemplates.SelectAllItems,
  },
  defaultPagination: {
    pageSize: 10,
    sortField: 'id',
    sortDirection: 'desc',
  },
};

export const agentInfoFormFieldsConfig: FormConfig = {
  fields: [
    {
      id: 'code',
      type: 'input',
      label: 'Code',
      required: true,
      requiredError: 'Code is required',
      allowAlphaNumeric: true,
      maxValue: 126,
    },
    {
      id: 'name',
      type: 'input',
      label: 'Name',
      required: true,
      requiredError: 'Name is required',
      maxValue: 126,
      allowAlphaNumericWithHyphenAmpersandParenthesesSpace: true
    },
    {
      id: 'types',
      type: 'input',
      label: 'Type',
      required: true,
      requiredError: 'Type is required',
    },
    {
      id: 'status',
      type: 'radio',
      label: 'Status',
      required: true,
      requiredError: 'Status is required',
    },
    {
      id: 'information',
      type: 'text',
      label: 'Information',
      required: false,
      requiredError: 'Information is required',
      maxValue: 500,
      isCheckForMaxValue: true,
      noInitialWhiteSpace: true,
    },
    {
      id: 'description',
      type: 'text',
      label: 'Description',
      required: false,
      requiredError: 'Description is required',
      maxValue: 500,
      isCheckForMaxValue: true,
      noInitialWhiteSpace: true,
    },
    {
      id: 'parcelCarrierId',
      type: 'select',
      label: 'Parcel Carrier',
      required: false,
    },
    {
      id: 'serviceTypes',
      type: 'select',
      label: 'Service Type',
      required: true,
      requiredError: 'Service Type is required',
    },
    {
      id: 'fuelSurcharge',
      type: 'text',
      label: 'Fuel Surcharges Markup(%)',
      required: true,
      requiredError: 'Fuel Surcharges is required',
      isDecimalField: true
    },
    {
      id: 'id',
      type: 'input',
      required: false,
      label: 'ID',
    },
  ],
};

export const agentAddressFormConfig: FormConfig = {
  fields: consigneeConfig.fields
    .filter(({ id }) => id !== 'name')
    .map(field =>
      field.id === 'email'
        ? {
            ...field,
            pattern: ValidatorPattern.Multi_Email_Validator,
            maxValue: 185,
            isCheckForMaxValue: true,
            required: true,
            requiredError: 'Email address is required',
          }
        : field
    ),
};

export const defaultAgentFilters: AgentFilter = {
  searchText: '',
  paging: {
    sortingOrder: 'desc',
    sortingField: 'id',
    fromIndex: 0,
    toIndex: 10,
  },
} as const;

export const agentExcelHeaders: string[] = [
  'Agent Code',
  'Agent Name',
  'Agent Type',
  'Status',
];

export const agentExcelColumnList: string[] = [
  'code',
  'name',
  'types',
  'status',
];

export const agentParcelCarrierMessages = {
  noneOptionLabel: 'None / Other agent',
  fieldLabel: 'Parcel Carrier',
} as const;

/** UI-only sentinel; maps to `parcelCarrierId: null` on save (D-APC-UI-004 / D-APC-UI-008). */
export const agentParcelCarrierNoneValue = 0;

export const agentInfoSectionHeaders: string[] = [
  'Agent Information',
  'Address',
];

export const agentInfoSectionHeaderKeys: string[] = ['agent', 'address'];

export const agentInfoHeader = [
  {
    label: 'Agent Code',
    key: 'code',
    defaultPrintWidth: 80,
    isSelected: true,
  },
  {
    defaultPrintWidth: 160,
    key: 'name',
    label: 'Agent Name',
    isSelected: true,
  },
  {
    label: 'Agent Types',
    isSelected: true,
    key: 'types',
    defaultPrintWidth: 180,
  },
  {
    label: 'Status',
    isSelected: true,
    key: 'status',
    defaultPrintWidth: 40,
  },
  {
    label: 'serviceTypes',
    isSelected: true,
    key: 'serviceTypes',
    defaultPrintWidth: 160,
  },
  {
    label: 'Fuel Surcharge',
    isSelected: true,
    key: 'fuelSurcharge',
    defaultPrintWidth: 160,
  },
  {
    label: 'Description',
    isSelected: true,
    key: 'description',
    defaultPrintWidth: 160,
  },
  {
    label: 'Information',
    isSelected: true,
    key: 'information',
    defaultPrintWidth: 160,
  },
  /* Agents - End */
];

export const agentInfoAddressHeaders = [
  {
    isSelected: true,
    key: 'countryCode',
    defaultPrintWidth: 160,
    label: 'Country Code',
  },
  {
    isSelected: true,
    key: 'companyName',
    defaultPrintWidth: 120,
    label: 'Company Name',
  },
  {
    key: 'address1',
    isSelected: true,
    defaultPrintWidth: 120,
    label: 'Address Line 1',
  },
  {
    label: 'Address Line 2',
    isSelected: true,
    defaultPrintWidth: 120,
    key: 'address2',
  },
  {
    isSelected: true,
    label: 'City',
    defaultPrintWidth: 80,
    key: 'city',
  },
  {
    label: 'State/Province/Region',
    defaultPrintWidth: 180,
    key: 'state',
    isSelected: true,
  },
  {
    label: 'Postal Code',
    defaultPrintWidth: 60,
    isSelected: true,
    key: 'zip',
  },
  {
    label: 'Phone',
    key: 'phone',
    defaultPrintWidth: 80,
    isSelected: true,
  },
  {
    defaultPrintWidth: 80,
    label: 'Email',
    isSelected: true,
    key: 'email',
  },
];

const isSelectedAndDefaultView = {
  isSelected: true,
  defaultView: true,
};

export const agentsColumnConfig: TableColumnsProp[] = [
  {
    ...isSelectedAndDefaultView,
    label: 'Name',
    key: 'name',
    defaultPrintWidth: 120,
  },
  {
    ...isSelectedAndDefaultView,
    key: 'code',
    label: 'Code',
    defaultPrintWidth: 100,
  },
  {
    ...isSelectedAndDefaultView,
    key: 'types',
    label: 'Types',
    defaultPrintWidth: 240,
  },
  {
    ...isSelectedAndDefaultView,
    key: 'status',
    label: 'Status',
    defaultPrintWidth: 120,
  },
];
